import { DateTime, Duration } from "luxon";
import { makeObservable, action, observable } from "mobx";

export class IntervalObservable {
  /**
   * The target timestamp, updated at each interval.
   */
  value = DateTime.now();

  /** Internal timeout reference so we can clear it later. */
  private _timeoutId: ReturnType<typeof setTimeout> | undefined;
  private _intervalMs: number;
  private _jitterMs: number;

  constructor({ duration, jitter }: { duration: Duration; jitter?: Duration }) {
    makeObservable<
      this,
      | "_timeoutId"
      | "_intervalMs"
      | "_jitterMs"
      | "value"
      | "start"
      | "scheduleNext"
      | "dispose"
      | "tick"
    >(this, {
      _timeoutId: false,
      _intervalMs: observable,
      _jitterMs: observable,
      value: observable,
      forceRefresh: action,
      start: action,
      scheduleNext: false,
      dispose: action,
      tick: action,
    });

    this._intervalMs = duration.toMillis();
    this._jitterMs = jitter?.toMillis() ?? 0;
    this.start();
  }

  private start = () => {
    this.scheduleNext();
  };

  private scheduleNext = () => {
    const delay = this._intervalMs + Math.random() * Math.min(this._intervalMs, this._jitterMs);
    this._timeoutId = setTimeout(() => {
      this.tick();
      this.scheduleNext();
    }, delay);
  };

  /** The method the interval calls each time. */
  private tick = () => {
    this.value = DateTime.now();
  };

  /**
   * We expose this primarily for testing purposes.
   */
  public forceRefresh = () => {
    this.tick();
  };

  /** Clean up the interval when you're done with it. */
  dispose = () => {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
    }

    this._timeoutId = undefined;
  };
}
